/*
  Performs all the logic required by a chatbot
*/

import Parser from '../utils/parser.js'
import { StreamingPromise } from '../utils/utils.js'
import $ from 'jquery'

const HOST = '/chatbot'
// const HOST = 'http://skills.iviet.com:8000/chatbot'
// export const HOST = 'http://localhost:5000/chatbot'

const DECODER = new TextDecoder('utf-8') // to decode UTF8

// Some states for parser calls
const READONLY_STATE = {
  local: 'readonly',
  context: 'readonly',
  contexts: 'readonly',
}
const LOCAL_STATE = {
  local: 'editable',
  context: 'readonly',
  contexts: 'readonly',
}
const OUTPUT_STATE = {
  local: 'readonly',
  context: 'editable',
  contexts: 'readonly',
}

// how to read various values
const VALUES = {
  'true': true,
  'false': false,
  'null': null,
}

// How to display various values
const TEXTS = {
  [true]: 'true',
  [false]: 'false',
  [null]: '',
  [undefined]: '',
}

const STATES_CALLBACK = {}
window.addEventListener('message', (event) => {
  if (!event.isTrusted) return
  if (!event.data.state) return
  const callback = STATES_CALLBACK[event.data.state]
  delete STATES_CALLBACK[event.data.state]
  if (callback) callback(event.data)
  event.source.close()
})

const DATE_REG = /^(20\d\d|19\d\d)-(0[1-9]|1[0-2])-(0[1-9]|[1-2]\d|3[0-1])$/
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const WEEKS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday']
const ORDINAL = ['st', 'nd', 'rd']
function DISP_DATE (m) {
  /* Displays a date in a user-friendly way */
  let date = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]))
  let parts = [], diff = Math.abs(new Date() - date)
  if (diff <= 14 * 24 * 3600 * 1000) {
    parts.push(WEEKS[date.getDay()])
  }
  parts.push(MONTHS[date.getMonth()])
  let day = date.getDate()
  if (day > 3 && day <= 20 || (day - 1) % 10 >= 3) parts.push(day + 'th')
  else parts.push(day + ORDINAL[day % 10 - 1])
  if (diff > 9 * 30 * 24 * 3600 * 1000) {
    parts.push((1900 + date.getYear()) + '')
  }
  return parts.join(' ')
}

export default class ChatBot {
  constructor (skill, cache) {
    this.skill = skill // the skill used
    this.cache = cache // the cache used
    this.variables = { [skill]: {} } // the variables of each context, as a dict
    this.contexts = { [skill]: [] } // the ordered list of all context, with ttl
    this.callbacks = [] // the waiting callbacks
    this.apicache = null // for api calls
  }

  compileIntent (intent) {
    /* Compiles an intent to ease computing */
    intent.entities = intent.entities.map(x => this.compileEntity(x))
    intent.actions = intent.actions.map(x => this.compileAction(x))
    intent.outputs = intent.outputs.map(x => this.compileOutput(x))
    intent.follow = intent.follow.map(x => this.compileFollow(x))
    intent.compiled = true
    return intent
  }

  compileEntity (entity) {
    /* Compiles entity objects */
    return {
      ...entity,
      value: entity.value && this.compileValue(entity.value),
    }
  }

  compileOutput (output) {
    /* Compiles context outputs */
    output = { ...output }
    if (output.script) {
      let parser = new Parser(output.script, null, OUTPUT_STATE)
      output.script = parser.parseProgram()
    }
    let variables = output.variables || {}
    output.variables = {}
    for (let name in variables) {
      let value = variables[name]
      if (value) {
        output.variables[name] = this.compileValue(value)
      }
    }
    return output
  }

  compileAction (action) {
    /* Compiles actions */
    if (action.type === 'script') {
      action = { ...action }
      let parser = new Parser(action.script, null, LOCAL_STATE)
      action.script = parser.parseProgram()
    }
    return action
  }

  compileFollow (follow) {
    /* Compiles intent follows */
    let variables = follow.variables || {}
    follow = {
      ...follow,
      condition: follow.condition && this.compileValue(follow.condition),
      variables: {},
    }
    for (let name in variables) {
      let value = variables[name]
      if (value) {
        follow.variables[name] = this.compileValue(value)
      }
    }
    if (follow.callback) {
      let variables = follow.callback.variables || {}
      let callback = {
        ...follow.callback,
        variables: variables,
      }
      for (let name in variables) {
        let value = variables[name]
        if (value) {
          callback.variables[name] = this.compileValue(value)
        }
      }
      follow.callback = callback
    }
    return follow
  }

  compileValue (value) {
    /* Compiles a scripting value */
    let parser = new Parser(value, null, READONLY_STATE)
    return parser.parseValueProgram()
  }

  newInput (data) {
    /* Treats new user data */
    let contexts = [] // the active contexts
    let globalContext = true
    for (let context of (this.contexts[this.skill] || [])) {
      contexts.push(context.context)
      if (context.exclusive) {
        globalContext = false
        break
      }
    }
    for (let context of this.callbacks) {
      contexts.push(context.context)
    }
    if (globalContext) contexts.push('')
    let intents = null // will be a dict of promises
    let buffer = [] // the list of received values
    let messages = new StreamingPromise() // the returned promise
    let first = false // if the first message has been received
    // request for a match
    fetch(new Request(HOST + '/match/' + (this.cache || 'test'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        skill: this.skill,
        data: data,
        contexts: contexts,
      }),
    })).then(response => {
      if (!response.ok) {
        throw new Error('invalid reponse status: ' +
          response.status + ' ' + response.statusText)
      }
      let reader = response.body.getReader()
      // getReader().read() returns a weird promise
      // similar to StreamingPromise
      // so better have a normal promise do deal with
      return new Promise((resolve, reject) => {
        // caled several time, once for each value, once done
        let onData = ({ done, value }) => {
          // buffer has been set to null, so stop processing
            if (!buffer) return
            // everything has arrived, reject the remaining promises
            // (in theory, it should be none)
            if (done) {
              console.log('done')
              if (buffer && buffer.length) {
                console.error('unfinished buffer', buffer)
              }
              buffer = null
              if (!first) {
                reject(new Error('empty response'))
              } else if (intents) {
                for (let intent in intents) {
                  let reject = intents[intent].reject
                  if (reject) reject(new Error('response ended prematurely'))
                }
              }
              return
            }
            // every JSON object ends with a "\n"
            console.log('value', DECODER.decode(value))
            value = DECODER.decode(value).split('\n')
            for (let index = 0; index < value.length; index++) {
              let val = value[index]
              if (val) buffer.push(val)
              // this one is not finished yet
              if (index >= value.length - 1) continue
              // parse the value
              val = $.trim((buffer).join(''))
              if (!val) continue
              try {
                val = JSON.parse(val)
              } catch (e) {
                console.error(e)
                reject(new Error('invalid response format'))
                return
              }
              buffer = []
              // no match found, end now
              if (!first && val.type === 'nomatch') {
                first = true
                messages.push({
                  type: 'nomatch',
                })
                messages.resolve()
                resolve()
              // // an answer, call callAnswer
              // } else if (!first && val.type === 'answer') {
              //   first = true
              //   this.callAnswer(val.variables, messages)
              //   resolve()
              // // a match, needs to wait for intents and call callIntent
              } else if (!first && (val.type === 'match' ||
                val.type === 'answer' || val.type === 'confirm')) {
                first = true
                // the intent promises
                intents = {}
                // function to get an intent promise
                let getIntent = (skill, intent) => {
                  console.log('getIntent', skill, intent)
                  let key = skill + ':' + intent
                  // already a promise, returns it
                  if (key in intents) return intents[key].promise
                  // no more data expected, reject
                  if (!buffer) return Promise.reject(new Error('no data'))
                  // create a new promise and return it
                  let promise = {}
                  promise.promise = new Promise((resolve, reject) => {
                    promise.resolve = resolve
                    promise.reject = reject
                  })
                  intents[key] = promise
                  return promise.promise
                }
                // if (!val.intent) {
                //   messages.push({
                //     type: 'message',
                //     text: 'you are back to skill ' + val.skill,
                //   })
                //   messages.resolve()
                //   return
                // }
                // call callIntent once the first intent has arrived
                getIntent(val.skill, val.intent).then(intent => {
                  let top = (this.contexts[this.skill] || [])[0]
                  if (top && top.context.substr(0, 'answer:'.length) ===
                    'answer:') {
                    this.contexts[this.skill].shift()
                    delete this.variables[this.skill][top.context]
                    if (val.type !== 'match') {
                      for (let name in top.variables) {
                        if (name in val.variables) continue
                        val.variables[name] = top.variables[name]
                      }
                    }
                  }
                  this.callIntent(intent, val.variables, getIntent, messages)
                }).then(resolve, reject)
              // an intent resolve its associated promise
              } else if (intents && val.type === 'intent') {
                console.log('intent', val.skill, val.intent)
                let key = val.skill + ':' + val.intent
                if (key in intents) {
                  let resolve = intents[key].resolve
                  if (resolve) resolve(val)
                  delete intents[key].reject
                // create one if none
                } else {
                  intents[key] = {
                    promise: Promise.resolve(val),
                  }
                }
              } else if (val.type === 'uncached') {
                console.error('uncached received: ' + val.path)
                messages.push(val)
              } else if (val.type === 'error') {
                console.error('error received: ' + val.error)
                messages.push({
                  type: 'error',
                  message: val.error,
                })
              } else {
                console.error('unexpected value of type "' + val.type + '"')
              }
            }
            reader.read().then(onData, onError)
          // something went wrong, stop processing
          }, onError = error => {
            buffer = null
            reject(error)
            if (intents) {
              for (let intent in intents) {
                let reject = intents[intent].reject
                if (reject) reject(error)
              }
            }
          }
        reader.read().then(onData, onError)
      })
    // something went wrong, reject the messages
    }).catch(messages.reject)
    return messages
  }

  // callAnswer (locals, messages) {
  //   /* Process an "answer" reply */
  //   console.log('callAnswer', this.answer.getIntent)
  //   locals = { ...this.answer.locals, ...locals }
  //   return this.callIntent(this.answer.intent, locals,
  //     this.answer.getIntent, messages, this.answer.context)
  // }

  callIntent (intent, locals, getIntent, messages, context) {
    /* Processes an intent
    - locals: dict of local variables
    - getIntent: function that returns a promise to any other intent
    - messages: a StreamingPromise to store the response messages
    - context: the context object of the intent */
    if (!intent.compiled) {
      intent = this.compileIntent(intent)
    }
    console.log('callIntent', intent, locals)
    // clean answer
    if (this.answer) {
      delete this.answer
      this.contexts[this.skill].shift()
    }
    this.skill = intent.skill
    if (!(this.skill in this.contexts)) {
      this.contexts[this.skill] = []
      this.variables[this.skill] = {}
    }
    // default values
    locals = locals || {}
    getIntent = getIntent || (() => Promise.reject(new Error('no data')))
    messages = messages || new StreamingPromise()
    if (context) context = context.variables
    else context = this.variables[this.skill][intent.context || ''] || {}
    // useful prefilled variables for scripts
    let varcontexts = {}
    for (let [ctxt, vars] of Object.entries(this.variables[this.skill])) {
      if (!(ctxt).match(/:/)) varcontexts[ctxt] = vars
    }
    let variables = {
      contexts: varcontexts,
      context: context,
      local: locals,
    }
    // check for entities default values and requirements
    let promise = Promise.resolve().then(() => {
      for (let entity of intent.entities) {
        // check that it's not null
        let value = locals[entity.name]
        if (value === undefined) value = null
        else if (value !== null) continue
        // look for default value
        if (entity.value) {
          value = entity.value.resolve(variables)
          if (value === undefined) value = null
          else if (value !== null) {
            locals[entity.name] = value
            continue
          }
        }
        // required yet no value
        if (entity.required) {
          // the question to ask to the user
          let question = 'What is the ' + entity.name
          if (entity.questions && entity.questions.length) {
            question = entity.questions[
              parseInt(Math.random() * entity.questions.length)]
            question = this.formatText(question, context, locals)
          }
          messages.push({
            type: 'message',
            text: question,
          })
          // create a context for the answer
          let answerContext = {
            context: 'answer:' + intent.intent + ':' +
              entity.name + ':' + entity.entity,
            variables: locals,
            ttl: 1,
            exclusive: true,
          }
          this.contexts[this.skill].unshift(answerContext)
          this.variables[this.skill][intent.intent] = locals
          // // save the state
          // this.answer = {
          //   intent: intent,
          //   locals: locals,
          //   getIntent: getIntent,
          //   context: context,
          // }
          // this.contextIntents[tempContext.context] = [tempIntent]
          return
        }
      }
    })
    // perform every action
    for (let action of intent.actions) {
      if (action.type === 'message') {
        promise = promise.then(() => {
          let text = action.texts[parseInt(action.texts.length * Math.random())]
          messages.push({
            type: 'message',
            text: this.formatText(text, context, locals),
          })
        })
      } else if (action.type === 'audio') {
        promise = promise.then(() => {
          messages.push({
            type: 'audio',
            url: action.urls[parseInt(action.urls.length * Math.random())],
          })
        })
      } else if (action.type === 'script') {
        promise = promise.then(() => {
          action.script.resolve(variables)
        })
      } else if (action.type === 'api') {
        const path = '/call/' + (this.cache || 'test') + '/' + intent.skill
        const redirect_uri = location.protocol + '//' + location.host +
            '/oauth2/callback'
        let callback
        callback = (data) => {
          const params = {}
          if (!data) {
          } else if (data.type === 'username-password') {
            params.username = data.username
            params.password = data.password
          } else if (data.type === 'redirect_uri') {
            params.redirect_uri = redirect_uri
            params.state = true
            params.code_verifier = true
          } else if (data.type === 'oauth2-authorization') {
            params.redirect_uri = redirect_uri
            params.code_verifier = data.code_verifier
            params.code = data.code
          } else {
            throw Error('unknown type ' + data.type)
          }
          return fetch(new Request(HOST + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: action,
              variables: variables,
              params: params,
              cache: this.apicache,
            }),
          })).then(response => response.json())
            .then(response => {
              console.log('api', response)
              if (response.cache) this.apicache = response.cache
              if (response.type === 'error') {
                throw new Error(response.result)
              } else if (response.type === 'username-password') {
                let message = {
                  type: 'username-password',
                  name: response.result.name,
                }
                message.promise = new Promise((resolve, reject) => {
                  message.resolve = resolve
                  message.reject = reject
                })
                messages.push(message)
                return message.promise.then(({ username, password }) => ({
                  type: 'username-password',
                  username: username,
                  password: password,
                })).then(callback)
              } else if (response.type === 'redirect_uri') {
                return callback(response)
              } else if (response.type === 'oauth2-authorization') {
                let message = {
                  type: 'oauth2-authorization',
                  name: response.result.name,
                  authorization_url: response.result.authorization_url,
                }
                message.promise = new Promise((resolve, reject) => {
                  STATES_CALLBACK[response.params.state] = resolve
                  message.reject = reject
                })
                messages.push(message)
                return message.promise.then(data => {
                  if (data.error) {
                    throw new Error(
                      data.error + ': ' + data.error_description)
                  }
                  if (data.code) return data
                  throw new Error('unexpected response')
                }).then(({ code }) => ({
                  type: 'oauth2-authorization',
                  code: code,
                  code_verifier: response.params.code_verifier,
                })).then(callback)
              } else if (response.type === 'result') {
                variables.local[action.variable || 'result'] = response.result
              }
            })
        }
        promise = promise.then(() => callback())
      }
    }

    let contexts = []
    let filter = {}
    let outputs = intent.outputs
    let followup = 'followup:' + intent.intent
    promise = promise.then(() => {
      if (!outputs.find(x => x.context === followup)) {
        outputs = [...outputs, { context: followup, ttl: 1, follow: true }]
      }
      // create output contexts
      for (let output of outputs) {
        filter[output.context] = true
        if (output.ttl === 0) continue
        let newcontext = {
          context: output.context,
          ttl: output.ttl > 0 ? output.ttl + 1 : output.ttl,
          exclusive: output.exclusive,
          variables: output.extend &&
            this.variables[this.skill][output.context] || {},
        }
        contexts.push(newcontext)
        if (output.follow) {
          for (let name in locals) {
            let value = locals[name]
            if (value !== null && value !== undefined) {
              newcontext.variables[name] = value
            }
          }
        }
        for (let name in output.variables || {}) {
          let value = output.variables[name].resolve(variables)
          if (value === null) delete newcontext.variables[name]
          else if (value !== undefined) newcontext.variables[name] = value
        }
        if (output.script) {
          output.script.resolve({
            ...variables,
            context: newcontext.variables,
          })
        }
      }
    })
    // look for a follow intent
    promise = promise.then(() => {
      for (let follow of intent.follow) {
        // verify the condition
        if (follow.condition && !follow.condition.condition(variables)) continue
        // compute the follow local variables
        let followVar = {}
        if (follow.follow) followVar = locals
        for (let name in (follow.variables || {})) {
          let value = follow.variables[name].resolve(variables)
          if (value === null) delete followVar[name]
          else if (value !== undefined) followVar[name] = value
        }
        // treat the callback
        if (follow.callback) {
          let callback = follow.callback
          let callbackVar = {}
          if (callback.follow) callbackVar = JSON.parse(JSON.stringify(locals))
          for (let name in (callback.variables || {})) {
            let value = callback.variables[name].resolve(variables)
            if (value === null) delete callbackVar[name]
            else if (value !== undefined) callbackVar[name] = value
          }
          let callbackContext = {
            context: 'callback:' + (follow.callback.skill || this.skill),
            ttl: -1,
            variables: callbackVar,
          }
          if (callback.intent) callbackContext.context += ':' + callback.intent
          this.callbacks.unshift(callbackContext)
        }
        let intentPromise = null
        if (follow.intent) {
          intentPromise = getIntent(follow.skill || this.skill, follow.intent)
        } else if (follow.exit === 'callback') {
          if (this.callbacks.length) {
            let callback = this.callbacks.shift()
            let context = callback.context.split(':')
            if (context.length <= 2) this.skill = context[1]
            else intentPromise = getIntent(context[1], context[2])
          } else {
            messages.push({
              type: 'error',
              message: 'end of chat',
            })
          }
        } else if (follow.exit === 'exit') {
          if (this.callbacks.length) {
            let callback = this.callbacks.shift()
            let context = callback.context.split(':')
            context.splice(0, 1, 'confirm')
            this.contexts[this.skill].unshift({
              context: context.join(':'),
              ttl: 1,
              exclusive: true,
            })
            messages.push({
              type: 'message',
              text: 'Do you want ot go back to skill ' + context[1],
            })
          } else {
            messages.push({
              type: 'error',
              message: 'end of chat',
            })
          }
        }
        for (let context of this.contexts[this.skill]) {
          if (filter[context.context]) continue
          contexts.push(context)
        }
        this.contexts[this.skill] = contexts
        this.variables[this.skill] = {}
        for (let context of contexts) {
          this.variables[this.skill][context.context] = context.variables
        }
        if (!intentPromise) {
          return
        }
        // call it once the intent is loaded
        return intentPromise.then(next_intent => {
          // do not reduce ttl
          return this.callIntent(next_intent, followVar, getIntent, messages)
        // catch any error
        })
      }
      // // everything is done, update contexts
      for (let context of this.contexts[this.skill]) {
        if (filter[context.context]) continue
        contexts.push(context)
      }
      this.contexts[this.skill] = contexts
      for (let skill in this.contexts) {
        let contexts = []
        let variables = {}
        for (let context of this.contexts[skill]) {
          if (context.ttl > 0) context.ttl -= 1
          if (!context.ttl) continue
          contexts.push(context)
          variables[context.context] = context.variables
        }
        this.contexts[skill] = contexts
        this.variables[skill] = variables
      }
    })

    promise = promise.then(messages.resolve, messages.reject)
    return promise.then(() => messages)
  }

  formatText (text, context, locals) {
    /* Turns a raw message into a displayable message */
    return text.replace(/([@$])(\w+)|\(([^)]+)\)|\{([^}]+)\}/g,
      (_, prefix, name, expression, tuned) => {
        let value
        if (prefix) {
          value = (prefix === '@' ? locals : context)[name]
        } else if (expression) {
          value = this.getValue(expression, context, locals)
        } else if (tuned) {
          return tuned
        }
        value = Array.isArray(value) ? value : [value]
        value = value.map(val => {
          if (val in TEXTS) return TEXTS[val]
          else if (typeof val === 'string') {
            let m = val.match(DATE_REG)
            if (m) return DISP_DATE(m)
            return val
          } else return val + ''
        }).filter(val => val)
        if (value.length <= 1) return value[0] || ''
        let out = []
        for (let i = 0; i < value.length; i += 2) {
          if (value.length >= i + 2) {
            out.push(value[i] + ' and ' + value[i + 1])
          } else {
            out.push(value[i])
          }
        }
        return out.join(', ')
      })
  }

  getValue (expression, context, locals) {
    /* replaces special occurances of formatText */
    let value = null
    for (let part of expression.split('|')) {
      let match = part.match(/^([@$])(\w+)$/)
      // eslint-disable-next-line no-cond-assign
      if (match = part.match(/^([@$])(\w+)$/)) {
        value = (match[1] === '@' ? locals : context)[match[2]]
      } else if (part.match(/^[0-9]+\.?|[0-9]*\.[0-9]+$/)) {
        value = parseFloat(part)
      // eslint-disable-next-line no-cond-assign
      } else if (match = part.match(/^(?:"([^']*)"|'([^']*)')$/)) {
        value = typeof match[1] === 'string' ? match[1] : match[2]
      } else if (part in VALUES) {
        value = VALUES[part]
      } else {
        value = part
      }
      if (value !== undefined && value !== null) return value
    }
    return value
  }

  addContext (skill, context) {
    context = {
      ...context,
      variables: {},
    }
    this.contexts[skill].unshift(context)
    this.variables[this.skill][context.context] = context.variables
  }
}
